import type { Capacidad } from '../../domain/policies/PoliticaAcceso';
import type { NombreIcono } from '../components/ui/iconos';

export interface EntradaNavegacion {
  /** Ruta relativa dentro de la aplicación. */
  ruta: string;
  etiqueta: string;
  icono: NombreIcono;
  /** Capacidad exigida para ver la entrada y entrar en la ruta. */
  capacidad: Capacidad;
  /** Texto de apoyo que se muestra al pasar el cursor. */
  descripcion: string;
}

export interface GrupoNavegacion {
  titulo: string;
  entradas: readonly EntradaNavegacion[];
}

/**
 * Estructura del menú.
 *
 * Es la única fuente de verdad: de aquí salen tanto los enlaces de la barra
 * lateral como la comprobación de permiso de cada ruta. En la versión anterior
 * eran dos listas separadas, y por eso varias secciones quedaban accesibles
 * escribiendo la dirección a mano aunque el menú las ocultara.
 */
export const NAVEGACION: readonly GrupoNavegacion[] = [
  {
    titulo: 'En vivo',
    entradas: [
      {
        ruta: '/',
        etiqueta: 'Panel',
        icono: 'dashboard',
        capacidad: 'ver_dashboard',
        descripcion: 'Vista general de la instalación',
      },
      {
        ruta: '/alarmas',
        etiqueta: 'Alarmas',
        icono: 'alarmas',
        capacidad: 'ver_alarmas',
        descripcion: 'Alarmas activas e historial',
      },
      {
        ruta: '/monitoreo',
        etiqueta: 'Monitoreo',
        icono: 'monitoreo',
        capacidad: 'ver_monitoreo',
        descripcion: 'Valores de sensores en tiempo real',
      },
      {
        ruta: '/control',
        etiqueta: 'Control',
        icono: 'control',
        capacidad: 'ver_control',
        descripcion: 'Encender y apagar equipos',
      },
      {
        ruta: '/energia',
        etiqueta: 'Energía',
        icono: 'energia',
        capacidad: 'ver_energia',
        descripcion: 'Consumo y medidores eléctricos',
      },
      {
        ruta: '/ubicaciones',
        etiqueta: 'Ubicaciones',
        icono: 'mapa',
        capacidad: 'ver_gps',
        descripcion: 'Mapa de personal en campo',
      },
      {
        ruta: '/camaras',
        etiqueta: 'Cámaras',
        icono: 'camara',
        capacidad: 'ver_camaras',
        descripcion: 'Videovigilancia',
      },
    ],
  },
  {
    titulo: 'Equipos',
    entradas: [
      {
        ruta: '/clientes',
        etiqueta: 'Clientes',
        icono: 'clientes',
        capacidad: 'ver_clientes',
        descripcion: 'Empresas atendidas',
      },
      {
        ruta: '/areas',
        etiqueta: 'Áreas',
        icono: 'areas',
        capacidad: 'ver_areas',
        descripcion: 'Zonas de cada cliente',
      },
      {
        ruta: '/nodos',
        etiqueta: 'Nodos',
        icono: 'nodos',
        capacidad: 'ver_nodos',
        descripcion: 'Equipos instalados en campo',
      },
      {
        ruta: '/sensores',
        etiqueta: 'Sensores',
        icono: 'sensores',
        capacidad: 'ver_sensores',
        descripcion: 'Puntos de medición',
      },
      {
        ruta: '/actuadores',
        etiqueta: 'Actuadores',
        icono: 'actuadores',
        capacidad: 'ver_actuadores',
        descripcion: 'Elementos controlables',
      },
      {
        ruta: '/medidores',
        etiqueta: 'Medidores',
        icono: 'medidor',
        capacidad: 'ver_medidores',
        descripcion: 'Medidores de energía',
      },
      {
        ruta: '/vistas',
        etiqueta: 'Vistas',
        icono: 'camara',
        capacidad: 'ver_vistas',
        descripcion: 'Cámaras de cada grabador',
      },
      {
        ruta: '/tipos-sensores',
        etiqueta: 'Tipos de sensores',
        icono: 'categoria',
        capacidad: 'ver_tipos_sensores',
        descripcion: 'Catálogo de tipos disponibles',
      },
      {
        ruta: '/tipos-actuadores',
        etiqueta: 'Tipos de actuadores',
        icono: 'categoria',
        capacidad: 'ver_tipos_actuadores',
        descripcion: 'Tipos permitidos por cliente',
      },
      {
        ruta: '/cuentas-ekm',
        etiqueta: 'Cuentas de medición',
        icono: 'llave',
        capacidad: 'ver_cuentas_ekm',
        descripcion: 'Credenciales de los medidores',
      },
      {
        ruta: '/dvr',
        etiqueta: 'DVR',
        icono: 'grabador',
        capacidad: 'ver_grabadores',
        descripcion: 'Grabadores de vídeo y su enlace',
      },
    ],
  },
  {
    titulo: 'Configuración',
    entradas: [
      {
        ruta: '/umbrales-analogicos',
        etiqueta: 'Umbrales analógicos',
        icono: 'umbrales',
        capacidad: 'ver_umbrales',
        descripcion: 'Rangos que disparan alarmas',
      },
      {
        ruta: '/umbrales-digitales',
        etiqueta: 'Umbrales digitales',
        icono: 'umbrales',
        capacidad: 'ver_umbrales',
        descripcion: 'Estados esperados y horarios',
      },
      {
        ruta: '/umbrales-energia',
        etiqueta: 'Umbrales de energía',
        icono: 'umbrales',
        capacidad: 'ver_umbrales',
        descripcion: 'Límites de los medidores',
      },
      {
        ruta: '/alarmas-energia',
        etiqueta: 'Alarmas de energía',
        icono: 'energia',
        capacidad: 'ver_umbrales',
        descripcion: 'Alarmas generadas por medidores',
      },
      {
        ruta: '/automatizacion',
        etiqueta: 'Automatización',
        icono: 'horario',
        capacidad: 'ver_automatizacion',
        descripcion: 'Reglas y horarios de actuadores',
      },
      {
        ruta: '/rutas',
        etiqueta: 'Rutas',
        icono: 'ruta',
        capacidad: 'ver_rutas',
        descripcion: 'Recorridos planificados',
      },
      {
        ruta: '/personal-campo',
        etiqueta: 'Personal en campo',
        icono: 'usuarios',
        capacidad: 'ver_gps_usuarios',
        descripcion: 'Trabajadores y puntos de interés',
      },
      {
        ruta: '/en-ruta',
        etiqueta: 'En ruta ahora',
        icono: 'cerca',
        capacidad: 'ver_en_ruta',
        descripcion: 'Seguimiento en vivo de recorridos',
      },
    ],
  },
  {
    titulo: 'Notificaciones',
    entradas: [
      {
        ruta: '/contactos',
        etiqueta: 'Contactos de alerta',
        icono: 'contactos',
        capacidad: 'ver_contactos',
        descripcion: 'A quién se avisa y por qué medio',
      },
      {
        ruta: '/escalamiento',
        etiqueta: 'Escalamiento',
        icono: 'escalamiento',
        capacidad: 'ver_escalamiento',
        descripcion: 'Niveles de aviso por severidad',
      },
      {
        ruta: '/envios',
        etiqueta: 'Envíos',
        icono: 'outbox',
        capacidad: 'ver_outbox',
        descripcion: 'Registro de notificaciones enviadas',
      },
      {
        ruta: '/integraciones',
        etiqueta: 'Integraciones',
        icono: 'webhook',
        capacidad: 'ver_webhooks',
        descripcion: 'Envío a sistemas externos',
      },
      {
        ruta: '/bitacora',
        etiqueta: 'Bitácora',
        icono: 'historial',
        capacidad: 'ver_bitacora',
        descripcion: 'Avisos que no pudieron entregarse',
      },
    ],
  },
  {
    titulo: 'Administración',
    entradas: [
      {
        ruta: '/usuarios',
        etiqueta: 'Usuarios',
        icono: 'usuarios',
        capacidad: 'ver_usuarios',
        descripcion: 'Altas, roles y permisos',
      },
      {
        ruta: '/reportes',
        etiqueta: 'Reportes programados',
        icono: 'reporte',
        capacidad: 'ver_config_reportes',
        descripcion: 'Informes mensuales automáticos',
      },
      {
        ruta: '/reportes-generados',
        etiqueta: 'Reportes generados',
        icono: 'descargar',
        capacidad: 'ver_reportes_generados',
        descripcion: 'Historial de informes en PDF',
      },
    ],
  },
];

/** Todas las entradas en una sola lista, para resolver rutas. */
export const ENTRADAS: readonly EntradaNavegacion[] = NAVEGACION.flatMap((g) => g.entradas);

export function buscarEntrada(ruta: string): EntradaNavegacion | undefined {
  return ENTRADAS.find((e) => e.ruta === ruta);
}
